import { cn } from '@/lib/utils'
import { useEffect } from 'react'
import Timeline from './Timeline'
import { Loader2 } from 'lucide-react'
import { useVideoTrim } from './useVideoTrim'
import { Button } from '@/components/ui/button'
import { CubeLoader } from '@/components/ui/cube-loader'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import ResponsiveImage from '@/components/mediaBuying/ResponsiveImage'

const Navigation = ({ goBack, goNext }) => {
    return (
        <div className="d-flex gap-2">
            <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                Back
            </Button>
            <Button onClick={goNext} className="tw:w-[156px] tw:!text-sm">
                Next
            </Button>
        </div>
    )
}

const VideoTrim = () => {
    const {
        getStepContentData,
        goToStep,
        setStepContentData,
        registerCustomNavigation,
        clearCustomNavigation,
        campaignFormat,
        currentStep,
        stepOrder,
        dataForStepper,
        markStepAs,
    } = useWizard()
    const { assetId } = getStepContentData()
    if (!assetId) goToStep('carouselCards')

    const {
        videoRef,
        timelineRef,
        isPlaying,
        isMuted,
        currentTime,
        trimStart,
        trimEnd,
        videoSrc,
        displayFrames,
        currentLength,
        recommendedLength,
        isLoading,
        isGeneratingFrames,
        togglePlay,
        toggleMute,
        handleMouseDown,
        handleTimelineClick,
        adAsset,
        showThumbnail,
    } = useVideoTrim(assetId)

    const goBack = () => {
        if (campaignFormat === 'carousel') goToStep('carouselCards')
        else goToStep('uploadAssets')
    }

    const goNext = () => {
        setStepContentData({ assetId }, 'setMediaCrop')
        setStepContentData({ assetId }, 'setVideoThumbnail')

        if (campaignFormat === 'video') {
            goToStep('setVideoThumbnail')
        } else if (campaignFormat === 'carousel') {
            goToStep('carouselCards')
        } else {
            markStepAs(1, currentStep)
            goToStep('setAdContent')
        }
    }

    // Custom navigation for media crop screen
    useEffect(() => {
        if (!adAsset) return

        registerCustomNavigation(<Navigation goBack={goBack} goNext={goNext} />)

        return () => clearCustomNavigation()
    }, [adAsset, currentStep, stepOrder.length])

    if (!adAsset)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    if (isLoading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    // Calculate aspect ratio and container height
    const aspectRatio = adAsset?.height && adAsset?.width ? adAsset.height / adAsset.width : 9 / 16
    // tw:w-lg is 32rem = 512px, tw:h-72 is 18rem = 288px max height
    const containerWidth = 512 // This is the width of tw:w-lg
    const maxHeight = 288 // This is tw:h-72
    const calculatedHeight = Math.min(maxHeight, containerWidth * aspectRatio)

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="videoTrim" />
            <div className="tw:flex-1 tw:p-4 tw:overflow-auto tw:text-white">
                <div className="tw:text-sm">
                    <label className="tw:mb-3 tw:text-white tw:block">
                        Slide the sides of the trimmer to adjust your video length.
                    </label>

                    {/* Video Player */}
                    <div className="tw:flex tw:items-center tw:justify-center">
                        <div className="tw:w-lg tw:flex tw:flex-col tw:gap-4 tw:mt-30">
                            <div className="tw:flex tw:justify-between tw:w-full">
                                <div className="tw:flex tw:items-center tw:gap-2">
                                    <span className="tw:font-semibold">Current length</span>
                                    <span>{currentLength}</span>
                                </div>
                                <div className="tw:flex tw:items-center tw:gap-2">
                                    <span className="tw:font-semibold">Recommended</span>
                                    <span>up to {recommendedLength} secs</span>
                                </div>
                            </div>
                            <div
                                className="tw:w-lg tw:overflow-hidden tw:relative"
                                style={{ height: `${calculatedHeight}px` }}
                            >
                                {showThumbnail && (
                                    <ResponsiveImage
                                        images={adAsset.thumbnails}
                                        alt="Video thumbnail"
                                        className="tw:w-full tw:h-full tw:object-contain"
                                    />
                                )}
                                <video
                                    ref={videoRef}
                                    src={videoSrc}
                                    muted={isMuted}
                                    className={cn('tw:w-full tw:h-full tw:object-contain', {
                                        'tw:hidden': showThumbnail,
                                    })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="tw:flex tw:flex-col tw:gap-4 tw:mt-1.5">
                        {isGeneratingFrames && (
                            <div className="tw:flex tw:items-center tw:justify-center">
                                <div className="tw:flex tw:flex-col tw:gap-0.5 tw:mt-4">
                                    <div className="tw:flex tw:gap-1 tw:text-left tw:items-center tw:gap-1">
                                        <Loader2 className="tw:h-3 tw:w-3 tw:animate-spin" />
                                        <span className="tw:text-sm tw:font-semibold">Processing...</span>
                                    </div>
                                    <span className="tw:text-xs">This usually takes 1-2 minutes...</span>
                                </div>
                            </div>
                        )}

                        {!isGeneratingFrames && (
                            <Timeline
                                togglePlay={togglePlay}
                                isPlaying={isPlaying}
                                toggleMute={toggleMute}
                                isMuted={isMuted}
                                handleMouseDown={handleMouseDown}
                                currentTime={currentTime}
                                trimStart={trimStart}
                                trimEnd={trimEnd}
                                frames={displayFrames}
                                framesCount={displayFrames.length}
                                timelineRef={timelineRef}
                                handleTimelineClick={handleTimelineClick}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default VideoTrim
